"use client";

import { useState, useEffect } from "react";
import { getEveningBrief, captureThought } from "@/lib/api";
import type { EveningRecap } from "@/lib/api";

export default function EveningRecapCard() {
  const [recap, setRecap] = useState<EveningRecap | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Evening reflection capture point (audit 2026-05-29: was dead static copy).
  const [reflection, setReflection] = useState("");
  const [reflectionSaved, setReflectionSaved] = useState(false);
  const [reflectionSaving, setReflectionSaving] = useState(false);

  useEffect(() => {
    getEveningBrief()
      .then(setRecap)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-4 mb-4 animate-pulse">
        <div className="h-4 bg-muted/10 rounded w-1/3 mb-3" />
        <div className="h-3 bg-muted/5 rounded w-2/3 mb-2" />
        <div className="h-3 bg-muted/5 rounded w-1/2" />
      </div>
    );
  }

  if (!recap) return null;

  const { completed_count, missed_count, mood_today, summary, completed_today, missed_tasks } = recap;

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden mb-4">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start justify-between p-4 text-left hover:bg-secondary/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sand-pale flex items-center justify-center">
            <span className="text-base">🌙</span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text">Evening Recap</h3>
            <p className="text-xs text-muted mt-0.5">{summary}</p>
          </div>
        </div>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-muted transition-transform mt-1 ${expanded ? "rotate-180" : ""}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Stats Bar */}
      <div className="flex items-center gap-4 px-4 pb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-accent font-bold">{completed_count}</span>
          <span className="text-xs text-muted">done</span>
        </div>
        {missed_count > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-amber font-bold">{missed_count}</span>
            <span className="text-xs text-muted">to carry</span>
          </div>
        )}
        {mood_today && (
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs text-muted">Mood</span>
            <span className="text-xs font-bold text-text">{mood_today.mood}/10</span>
            <span className="text-xs text-muted">Energy</span>
            <span className="text-xs font-bold text-text">{mood_today.energy}/10</span>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          {/* Completed Tasks */}
          {completed_today.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-accent mb-1.5">Completed</p>
              <ul className="space-y-1">
                {completed_today.map((task) => (
                  <li key={task.id} className="flex items-center gap-2 text-sm text-text-light">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent flex-shrink-0">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{task.title}</span>
                    {task.actual_minutes && (
                      <span className="text-xs text-muted ml-auto">{task.actual_minutes}m</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Missed / Carry Forward */}
          {missed_tasks.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber mb-1.5">Carry Forward</p>
              <ul className="space-y-1">
                {missed_tasks.map((task) => (
                  <li key={task.id} className="flex items-center gap-2 text-sm text-text-light">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-amber flex-shrink-0">
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                    <span>{task.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Reflection prompt — a real capture point (persists to thoughts) */}
          <div className="bg-sand-pale/50 rounded-xl p-3 mt-2">
            <p className="text-xs text-earth italic mb-2">
              What was the best thing about today? Even small wins count.
            </p>
            {reflectionSaved ? (
              <p
                data-testid="evening-reflection-saved"
                className="text-xs font-medium text-accent"
              >
                Saved — thanks for reflecting. 🌙
              </p>
            ) : (
              <>
                <textarea
                  data-testid="evening-reflection-input"
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="A small win, a moment, anything…"
                  rows={2}
                  aria-label="Evening reflection"
                  className="w-full rounded-lg border border-border bg-white px-2 py-1.5
                             text-xs text-text focus:outline-none focus:ring-2
                             focus:ring-forest-med/40"
                />
                <button
                  type="button"
                  data-testid="evening-reflection-save"
                  disabled={!reflection.trim() || reflectionSaving}
                  onClick={async () => {
                    setReflectionSaving(true);
                    try {
                      await captureThought(reflection.trim());
                      setReflectionSaved(true);
                    } catch {
                      // Keep the text so the user can retry.
                    } finally {
                      setReflectionSaving(false);
                    }
                  }}
                  className="mt-1.5 rounded-full bg-forest-med px-3 py-1 text-[11px]
                             font-medium text-white transition-colors hover:bg-forest
                             disabled:opacity-50 motion-reduce:transition-none"
                >
                  {reflectionSaving ? "Saving…" : "Save reflection"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
